<template>
  <card card-body-classes="table-full-width" no-footer-line>
    <h4 slot="header" class="card-title">{{ $t("message.invoices") }}</h4>
    <div class="card-body">
      <b-form class="d-flex mb-2" inline @submit.prevent>
        <b-form-select
          v-model="pagination.perPage"
          aria-controls="invoices__table"
          :options="pagination.perPageOptions"
        >
        </b-form-select>
        <fg-input
          id="invoices__searchInput"
          class="ml-2"
          v-model="searchQuery"
          placeholder="Search..."
          aria-label="Search"
          aria-controls="invoices__table"
        ></fg-input>
        <router-link class="ml-auto mt-0 btn btn-primary" to="/invoices/add">
          {{ $t("message.add") }}
        </router-link>
      </b-form>
      <b-table
        id="invoices__table"
        :items="invoices"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter="searchQuery"
        :per-page="pagination.perPage"
        :current-page="pagination.currentPage"
        :busy="isLoading"
        :show-empty="!isLoading && !invoices.length"
        stacked="md"
        striped
        @filtered="filteredItems => (searchItemsLength = filteredItems.length)"
      >
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template v-slot:empty>
          <div v-if="error" class="text-center text-danger my-2">
            <p>{{ $t("message.somethingWentWrong") }}</p>
            <p>{{ error }}</p>
          </div>
          <div v-else class="text-center text-primary my-2">
            <p>{{ $t("message.noInvoices") }}</p>
          </div>
        </template>
        <template v-slot:cell(name)="data">
          <router-link
            tag="a"
            :to="`/management/employees/edit/${data.item._id}`"
            >{{ data.item.name }}</router-link
          >
        </template>
        <template v-slot:cell(number)="data">
          <router-link tag="a" :to="`/invoices/view/${data.item._id}`">{{
            data.item.number
          }}</router-link>
        </template>
        <template v-slot:cell(to)="data">
          <router-link
            v-if="data.item.to && data.item.to._id"
            tag="a"
            :to="`/customers/view/${data.item.to._id}`"
            >{{ data.item.to.companyName }}</router-link
          >
          <span v-else>{{
            data.item.to && data.item.to.companyName
          }}</span>
        </template>
        <template v-slot:cell(actions)="data">
          <drop-down
            class="row-actions"
            tag="button"
            position="right"
            icon="fa fa-ellipsis-v"
            hideArrow
          >
            <router-link
              tag="a"
              class="dropdown-item"
              :to="`/invoices/view/${data.item._id}`"
              >{{ $t("message.view") }}</router-link
            >
            <button class="dropdown-item" @click="handleDownloadPdf(data.item)">
              {{ $t("message.downloadPdf") }}
            </button>
            <button
              class="dropdown-item"
              :disabled="!$can('update', 'INVOICES')"
              @click="handleSetStatusToPaid(data.item)"
            >
              {{ $t("message.invoiceSetStatusToPaid") }}
            </button>
            <button
              class="dropdown-item"
              :disabled="!$can('create', 'CREDIT_NOTES')"
              @click="onGenerateCreditNote(data.item)"
            >
              {{ $t("message.generateCreditNote") }}
            </button>
          </drop-down>
        </template>
      </b-table>
      <div class="d-flex justify-content-between flex-wrap">
        <div>
          <span class="card-category">
            Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
          </span>
        </div>
        <n-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        />
      </div>
    </div>
  </card>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { Pagination as NPagination } from "src/components";
import { dateToHuman } from "@/helpers/helpers";
import { downloadInvoicePdf } from "@/helpers/documents";

export default {
  components: {
    NPagination
  },
  data() {
    return {
      loader: null,
      fields: [
        {
          key: "number",
          label: this.$t("message.invoiceNumber"),
          formatter: dateToHuman,
          sortable: true
        },
        {
          key: "to",
          label: this.$t("message.invoiceTo"),
          sortable: true
        },
        {
          key: "sentDate",
          label: this.$t("message.dateSent"),
          formatter: dateToHuman,
          sortable: true
        },
        {
          key: "dueDate",
          label: this.$t("message.dateDue"),
          formatter: dateToHuman,
          sortable: true
        },
        {
          key: "total",
          label: this.$t("message.invoiceTotal"),
          formatter: value => (value ? value.displayValue : ""),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("message.actions"),
          sortable: false
        }
      ],
      searchQuery: null,
      searchItemsLength: 0,
      sortBy: "number",
      sortDesc: true,
      pagination: {
        perPage: 5,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50]
      },
      isLoading: false,
      error: null
    };
  },
  methods: {
    ...mapActions(["fetchInvoices", "setInvoiceStatusToPaid"]),
    async handleDownloadPdf(invoice) {
      this.loader = this.$loading.show();
      await downloadInvoicePdf(invoice);
      this.loader.hide();
    },
    async handleSetStatusToPaid(invoice) {
      const result = await this.$swal({
        title: this.$t("message.areYouSure"),
        text: this.$t("message.invoiceSetStatusToPaidConfirmationText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("message.confirm"),
        cancelButtonText: this.$t("message.goBack")
      });

      if (!result.value) return;

      this.loader = this.$loading.show();

      try {
        await this.setInvoiceStatusToPaid(invoice);
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          message: this.$t("message.invoiceSetStatusToPaidSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.invoiceSetStatusToPaidFailure") +
            `${error.message}.`
        });
      } finally {
        this.loader.hide();
      }
    },
    getInvoiceStatusClass(invoice) {
      const { status, dueDate } = invoice;

      // Paid
      if (status === "PAID") return "success";
      if (status === "CANCELLED") return "warning";

      const today = moment();
      const due = moment(dueDate);

      const diff = due.diff(today);

      // Late
      if (diff < 0) return "danger";

      // Open
      return "info";
    },
    async onGenerateCreditNote(invoice) {
      if (invoice.stripeRefundProcessed) {
        const confirmationResult = await this.$swal({
          title: this.$t("message.areYouSure"),
          text: this.$t("message.invoiceAlreadyCreditedConfirmationText"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("message.continue"),
          cancelButtonText: this.$t("message.cancel")
        });

        if (!confirmationResult.value) return;
      }

      this.$router.push(`/credit-notes/generate/${invoice._id}`);
    },
    showLoadingIndicator() {
      this.isLoading = true;
    },
    hideLoadingIndicator() {
      this.isLoading = false;
    },
    showError(error) {
      this.error = error;
    }
  },
  computed: {
    ...mapGetters(["invoices"]),
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchQuery ? this.searchItemsLength : this.invoices.length;
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    }
  },
  filters: {
    invoiceStatus(invoice) {
      const { status, dueDate } = invoice;

      if (status === "PAID") return "Paid";
      if (status === "CANCELLED") return "Uncollectable";

      const today = moment();
      const due = moment(dueDate);

      const diff = due.diff(today);

      if (diff < 0) return "Late";

      return "Open";
    }
  },
  async created() {
    try {
      this.showLoadingIndicator();
      await this.fetchInvoices();
    } catch (e) {
      this.showError(e.message);
    } finally {
      this.hideLoadingIndicator();
    }
  }
};
</script>
